import { useEffect, useRef, useState } from "react";
import type {
  Lead,
  LeadFormOptionsResponse,
  WorkflowPipelineItem,
} from "@crewfsm/contracts";
import {
  Button,
  Label,
  NativeSelect,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Skeleton,
} from "@crewfsm/ui/app";
import { PipelineApiError, type PipelineApi } from "./pipeline-api";
import {
  allowedPipelineTransitions,
  isPipelineStatus,
  PIPELINE_STATUS_LABELS,
  type PipelineStatus,
} from "./pipeline-model";

export type LeadAction = "LEAD_STATUS" | "LEAD_ASSIGN" | "LEAD_ARCHIVE";
export const LEAD_ACTION_LABELS: Record<LeadAction, string> = {
  LEAD_STATUS: "Change lead status",
  LEAD_ASSIGN: "Assign lead",
  LEAD_ARCHIVE: "Archive lead",
};
export function leadActionsFor(
  item: WorkflowPipelineItem,
  permissions: readonly string[],
): LeadAction[] {
  if (
    item.resource.type !== "LEAD" ||
    !isPipelineStatus(item.status as Lead["status"])
  )
    return [];
  return (
    [
      ["leads:write", "LEAD_STATUS"],
      ["leads:assign", "LEAD_ASSIGN"],
      ["leads:archive", "LEAD_ARCHIVE"],
    ] as const
  )
    .filter(([permission]) => permissions.includes(permission))
    .map(([, action]) => action);
}
export function isLeadAction(action: string): action is LeadAction {
  return Object.hasOwn(LEAD_ACTION_LABELS, action);
}

export function LeadActionPanel({
  item,
  action,
  api,
  onClose,
  onSaved,
  onReturnFocus,
}: {
  item: WorkflowPipelineItem;
  action: LeadAction;
  api: PipelineApi;
  onClose: () => void;
  onSaved: (message: string) => void;
  onReturnFocus: () => void;
}) {
  const [lead, setLead] = useState<Lead | null>(null);
  const [members, setMembers] = useState<
    LeadFormOptionsResponse["teamMembers"]
  >([]);
  const [status, setStatus] = useState<PipelineStatus | "">("");
  const [assignee, setAssignee] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stale, setStale] = useState(false);
  const [revision, setRevision] = useState(0);
  const saving = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    let current = true;
    setLoading(true);
    setError(null);
    setStale(false);
    setLead(null);
    void Promise.all([
      api.lead(item.resource.id),
      action === "LEAD_ASSIGN" ? api.formOptions() : Promise.resolve(null),
    ])
      .then(([value, options]) => {
        if (!current) return;
        setLead(value);
        setMembers(options?.teamMembers ?? []);
        setAssignee(value.assignedToUserId ?? "");
        setStatus("");
      })
      .catch((cause: unknown) => {
        if (current) setError(message(cause));
      })
      .finally(() => {
        if (current) setLoading(false);
      });
    return () => {
      current = false;
    };
  }, [api, item.resource.id, action, revision]);
  const mutable = lead && isPipelineStatus(lead.status);
  const transitions = lead ? allowedPipelineTransitions(lead.status) : [];
  const save = async () => {
    if (!lead || !mutable || saving.current || stale || loading) return;
    if (action === "LEAD_STATUS" && (!status || !transitions.includes(status)))
      return;
    saving.current = true;
    setBusy(true);
    setError(null);
    try {
      if (action === "LEAD_STATUS" && status)
        await api.updateStatus(lead.id, status);
      else if (action === "LEAD_ASSIGN")
        await api.assign(lead.id, assignee || null);
      else if (action === "LEAD_ARCHIVE") await api.archive(lead.id);
      if (mounted.current)
        onSaved(
          action === "LEAD_STATUS"
            ? "Lead status updated."
            : action === "LEAD_ASSIGN"
              ? "Lead assignment updated."
              : "Lead archived.",
        );
    } catch (cause) {
      if (mounted.current) {
        setError(message(cause));
        setStale(
          cause instanceof PipelineApiError &&
            [403, 404, 409].includes(cause.status),
        );
      }
    } finally {
      saving.current = false;
      if (mounted.current) setBusy(false);
    }
  };
  return (
    <Sheet
      open
      onOpenChange={(open) => {
        if (!open && !saving.current) onClose();
      }}
    >
      <SheetContent
        className="workflow-theme workflow-action-panel gap-0 data-[side=right]:w-full data-[side=right]:sm:max-w-none data-[side=right]:md:max-w-sm"
        showCloseButton={!busy}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          onReturnFocus();
        }}
        onInteractOutside={(event) => {
          if (saving.current) event.preventDefault();
        }}
        onEscapeKeyDown={(event) => {
          if (saving.current) event.preventDefault();
        }}
      >
        <SheetHeader className="border-b p-5 pr-14">
          <SheetTitle>{LEAD_ACTION_LABELS[action]}</SheetTitle>
          <SheetDescription className="mt-2 [overflow-wrap:anywhere]">
            {item.title} · {item.customerName}
          </SheetDescription>
        </SheetHeader>
        <form
          className="flex min-h-0 flex-1 flex-col"
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <div className="min-h-0 flex-1 overflow-y-auto p-5">
            {loading ? (
              <div
                role="status"
                aria-label="Loading lead details"
                className="space-y-3"
              >
                <Skeleton className="h-4 w-28" />
                <Skeleton className="h-10 w-full" />
              </div>
            ) : null}
            {error ? (
              <div role="alert" className="mb-5 space-y-3 text-sm">
                <p>{error}</p>
                {stale || !lead ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setRevision((value) => value + 1)}
                  >
                    {stale ? "Reload details" : "Try again"}
                  </Button>
                ) : null}
              </div>
            ) : null}
            {!loading && lead && !mutable ? (
              <p role="status" className="text-sm text-muted-foreground">
                This lead has been converted or archived. Open its detail page
                to continue.
              </p>
            ) : null}
            {mutable ? (
              <fieldset
                disabled={busy || stale || loading}
                className="min-w-0 space-y-5 disabled:opacity-60"
              >
                {action === "LEAD_STATUS" ? (
                  <div className="space-y-2">
                    <Label htmlFor="pipeline-lead-status">Lead status</Label>
                    <NativeSelect
                      id="pipeline-lead-status"
                      autoFocus
                      required
                      className="w-full [&_select]:h-10"
                      value={status}
                      onChange={(event) =>
                        setStatus(event.target.value as PipelineStatus)
                      }
                    >
                      <option value="">Choose status</option>
                      {transitions.map((value) => (
                        <option key={value} value={value}>
                          {PIPELINE_STATUS_LABELS[value]}
                        </option>
                      ))}
                    </NativeSelect>
                    <p className="text-sm text-muted-foreground">
                      Current status:{" "}
                      {PIPELINE_STATUS_LABELS[lead.status as PipelineStatus]}.
                    </p>
                  </div>
                ) : null}
                {action === "LEAD_ASSIGN" ? (
                  <div className="space-y-2">
                    <Label htmlFor="pipeline-lead-assignee">
                      Lead assignee
                    </Label>
                    <NativeSelect
                      id="pipeline-lead-assignee"
                      autoFocus
                      className="w-full [&_select]:h-10"
                      value={assignee}
                      onChange={(event) => setAssignee(event.target.value)}
                    >
                      <option value="">Unassigned</option>
                      {assignee &&
                      !members.some((member) => member.userId === assignee) ? (
                        <option value={assignee} disabled>
                          Previous assignee unavailable
                        </option>
                      ) : null}
                      {members.map((member) => (
                        <option key={member.userId} value={member.userId}>
                          {member.displayName ?? member.email ?? "Team member"}
                        </option>
                      ))}
                    </NativeSelect>
                    <p className="text-sm text-muted-foreground">
                      Assign the person handling this lead. The office owner is
                      managed separately.
                    </p>
                  </div>
                ) : null}
                {action === "LEAD_ARCHIVE" ? (
                  <p className="text-sm text-muted-foreground">
                    Move this lead to Closed. Its details and history will
                    remain available.
                  </p>
                ) : null}
              </fieldset>
            ) : null}
          </div>
          <div className="flex justify-end gap-2 border-t p-5">
            <Button variant="outline" disabled={busy} onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant={action === "LEAD_ARCHIVE" ? "destructive" : "default"}
              disabled={
                loading ||
                busy ||
                stale ||
                !mutable ||
                (action === "LEAD_STATUS" && !status)
              }
            >
              {busy
                ? "Saving…"
                : action === "LEAD_ARCHIVE"
                  ? "Archive lead"
                  : "Save"}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
function message(cause: unknown) {
  if (cause instanceof PipelineApiError && cause.status === 409)
    return "This lead changed. Reload its details before trying again.";
  return cause instanceof Error
    ? cause.message
    : "The lead could not be updated. Try again.";
}
